<template>
    <div class="container table-template">
        <div class="row mb-4">
            <div class="col">
                <span v-if="partner" class="partner">{{partner.name}}</span>
            </div>
            <div class="col clearfix">
                <button type="button" v-if="add_code_button" @click="onOpenCreateCodeModal()" class="float-right btn-orange-blue" data-toggle="modal" data-target="#content-modal">Add Code</button>
            </div>
        </div>

        <div class="row mb-2">
            <div class="col">
                <div class="custom-control custom-control-inline custom-checkbox">
                    <input type="checkbox" id="showDisabledCheckbox" v-model="showDisabled" class="custom-control-input" />
                    <label for="showDisabledCheckbox" class="custom-control-label">Show Disabled Codes</label>
                </div>
            </div>
        </div>

        <div horizontal label="Filter" class="form-group d-md-none not-generic-form" id="codeOptions">
            <div class="input-group">
                <select v-model="sortField.key">
                    <option :value="null">-- Filter --</option>
                    <option v-for="option in sortCodeOptions" :value="option.value">{{ option.text }}</option>
                </select>
                <select :disabled="!sortField.key" v-model="sortDesc">
                    <option :value="false">Asc</option>
                    <option :value="true">Desc</option>
                </select>
            </div>

            <div class="input-group border-search">
                <input type="text" v-model="searchString" placeholder="Type to Search" />
                <div class="input-group-append">
                    <i class="fa fa-search my-auto"></i>
                </div>
            </div>
        </div>

        <div class="row rounded-top reg-codes">
            <div class="lock-codes">
                <img :src="$basePath + '/images/lock.png'" />
                <span class="table-title">Registration Codes</span>
            </div>
            <div class="border-search d-none d-md-block">
                <div class="input-group">
                    <input type="text" class="form-control" v-model="searchString" placeholder="Type to Search" />
                    <div class="input-group-append">
                        <i class="fa fa-search my-auto"></i>
                    </div>
                </div>
            </div>
        </div>

        <transition name="fade">
            <div v-if="!loading" key="table">
                <div class="row justify-content-center">
                    <table class="table">
                        <thead>
                            <tr>
                                <th v-for="field in codeFields" :key="field.key" @click="sortBy(field)" :style="field.thStyle">
                                    {{ field.label }}
                                    <template v-if="field.sortable">
                                        <span v-show="sortField?.key != field.key"><i class="fa fa-sort" aria-hidden="true"></i></span>
                                        <span v-show="sortField && sortField.key == field.key && sortDesc"><i class="fa fa-sort-down" aria-hidden="true"></i></span>
                                        <span v-show="sortField && sortField.key == field.key && !sortDesc"><i class="fa fa-sort-up" aria-hidden="true"></i></span>
                                    </template>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in paginatedItems">
                                <td>{{ item.code }}</td>
                                <td>{{ (item.partner) ? item.partner.name : '--' }}</td>
                                <td>{{ (item.ta_consultant) ? item.ta_consultant.name : '--' }}</td>
                                <td>{{ item.invites_sent }}</td>
                                <td>{{ item.code_uses }}</td>
                                <td>{{ item.status }}</td>
                                <td>{{ (item.partner.inactive) ? 'Inactive' : 'Active' }}</td>
                                <td>
                                    <button type="button" @click="onShowInvitesModal(item)"
                                        class="btn btn-sm btn-primary mt-1 mr-1"
                                        data-toggle="modal" data-target="#content-modal"
                                    >
                                        Invites
                                    </button>
                                    <button type="button"
                                        v-if="userRole == 'site-admin' || userRole == 'super-user'"
                                        @click="onShowEditCodeModal(item)"
                                        class="btn btn-sm btn-success mt-1"
                                        data-toggle="modal" data-target="#content-modal"
                                    >
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="row pagination-row justify-content-center">
                    <nav aria-label="Page navigation for list of Connected Programs">
                        <ul class="pagination" role="menubar">
                            <li class="page-item">
                                <a class="page-link" @click="prevPage" aria-label="Go to previous page">&lt;</a>
                            </li>
                            <li v-for="pageNum in pageCount" class="page-item" :class="{active: pageNum == currentPage}" :aria-label="`Go to page ${pageNum}`">
                                <a class="page-link" @click="setPage(pageNum)">{{ pageNum }}</a>
                            </li>
                            <li class="page-item">
                                <a class="page-link" @click="nextPage" aria-label="Go to next page">&gt;</a>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
            <div class="row justify-content-center border border-info border-top-0" v-else key="loading">
                <div class="fa-3x">
                    <i class="fas fa-spinner fa-pulse" style="color:#91ccda;"></i>
                </div>
            </div>
        </transition>
    </div>
</template>

<style lang="scss">
    @import "../../../sass/layout/tables";
</style>

<style>
    .btn-success {
        color: #fff;
    }

    .button-container {
        display: flex;
    }

    /* Let the buttons grow and fill the available space equally */
    .button-container .btn {
        flex-grow: 1;

        /* Add margin to create spacing between buttons */
        margin-right: 5px; /* Adjust the value as needed */
    }

    /* Remove the margin from the last button to avoid extra space at the end */
    .button-container .btn:last-child {
        margin-right: 0;
    }
</style>



<script>
    import urlPolyfill from 'url-polyfill'
    import _ from 'lodash';
    import { mapWritableState } from 'pinia'
    import { useRegistrationCodesStore } from './store.js';

    export default {
        name: 'RegistrationCodeList',
        components: {
        },
        props: [
            'user-role', 
            'user-name', 
            'user-email'
        ],
        data() {
            return {
                codes: [],
                partners: [],
                consultants: [],
                codeFields: [
                    { 
                        key: 'code', 
                        label: 'Code', 
                        sortable: true,
                        compareFn: this.compareDefault,
                        thStyle: {
                            width: '20%'
                        }
                    },
                    { 
                        key: 'partner', 
                        label: 'Partner', 
                        sortable: true,
                        compareFn: this.compareByKey('partner.name')
                    },
                    { 
                        key: 'ta_consultant', 
                        label: 'TA Consultant', 
                        sortable: true,
                        compareFn: this.compareByKey('ta_consultant.name')
                    },
                    { 
                        key: 'invites_sent', 
                        label: 'Invites Sent', 
                        sortable: true,
                        compareFn: this.compareDefault,
                        thStyle: {
                            width: '75px',
                        }
                    },
                    { 
                        key: 'code_uses', 
                        label: 'Code Uses', 
                        sortable: true,
                        compareFn: this.compareDefault,
                        thStyle: {
                            width: '75px',
                        }
                    },
                    { 
                        key: 'status', 
                        label: 'Code Status', 
                        sortable: true,
                        thStyle: {
                            width: '75px',
                        }
                    },
                    {
                        key: 'partner_status',
                        label: 'Partner Status',
                        sortable: true,
                        thStyle: {
                            width: '75px',
                        }
                    },
                    { 
                        key: 'actions', 
                        label: 'Actions'
                     }
                ],
                currentPage: 1,
                perPage: 10,
                totalRows: null,
                add_code_button: null,
                invitesCode: null,
                refreshInvites: {},
                invite: {},
                sortDesc: true,
                searchString: null,
                partner: null,
                loading: true,
                showDisabled: false,
                sortField: {
                    key: null,
                    label: null,
                    sortable: false,
                    compareFn: undefined
                },
            }
        },
        methods: {
            onOpenCreateCodeModal() {
                this.currentComponent = 'CreateCodeForm';
                this.title = "Create Registration Code";
                this.showModal = true;

            }, 
            onShowEditCodeModal(selectedCode) {
                this.currentComponent = 'EditCodeForm';
                this.selectedCode = selectedCode;
                this.title = "Edit Registration Code";
                this.showModal = true;
            },
            onShowInvitesModal(selectedCode){
                this.currentComponent = 'Invites';
                this.selectedCode = selectedCode;
                this.title = `Invites for code ${selectedCode.code}`;
            }, 
            getCodes(params = {}) {
                var url = '/api/registration-codes'
                var partnerId = new URL(window.location).searchParams.get("partner_id")
                let query = params;
                let queryString = '';
                var queryDict = {};
                location.search
                    .substr(1)
                    .split("&")
                    .forEach((item) => {
                        queryDict[item.split("=")[0]] = item.split("=")[1]
                    });
                if(queryDict.hasOwnProperty('partner_id')){
                    var partnerId = queryDict.partner_id;
                } else {
                    var partnerId = null;
                }
                if(partnerId){
                    query.partner_id = partnerId
                }
                if (Object.keys(query).length > 0) {
                    queryString = '?'+Object.keys(query).map(key => key + '=' + query[key]).join('&');
                }

                return axios.get(url+queryString)
                    .then((response) => {
                        this.loading = false
                        this.codes = response.data.data
                        this.totalRows = this.codes.length
                        return response
                    })
                    .catch(function (error) {
                        console.error(error)
                        return error
                    })
            },
            getPartners() {
                axios.get('/api/partners')
                .then(function (response) {
                    this.partners = response.data.data
                }.bind(this))
                .catch(function (error) {
                    console.error(error)
                })
            },
            getConsultants() {
                axios.get('/api/consultants')
                .then(function (response) {
                    this.consultants = response.data.data
                }.bind(this))
                .catch(function (error) {
                    console.error(error)
                })
            },
            setPage: function(page) {
                this.currentPage = page;
            },
            nextPage: function() {
                if(this.currentPage >= this.pageCount) {
                    this.currentPage = this.pageCount;
                } else {
                    this.currentPage++;
                }
            },
            prevPage: function() {
                if(this.currentPage <= 1) {
                    this.currentPage = 1;
                } else {
                    this.currentPage--;
                }
            },
            resetPage: function() {
                this.setPage(1);
            },
            sortBy(field) {
                if(field.sortable) {
                    if(field.key === this.sortField.key) {
                        this.toggleSortDir();
                    } else {
                        this.sortDesc = false;
                    }
                    this.sortField = field;
                    this.resetPage();
                }
            },
            toggleSortDir() {
                this.sortDesc = !this.sortDesc
            },
            compareDefault: function(a, b) {
                if(a[this.sortField.key] < b[this.sortField.key])
                    return -1;
                if(a[this.sortField.key] > b[this.sortField.key])
                    return 1;
                return 0;
            },
            compareDate: function(a, b) {
                let aDate = new Date(a[this.sortField.key]);
                let bDate = new Date(b[this.sortField.key]);
                return aDate - bDate;
            },
            /**
             * Return compare function using @param key to get value from objects being compared
             * @param key
             */
            compareByKey: function(key) {
                return function(a,b) {
                    let aVal = _.get(a, key)
                    let bVal = _.get(b, key)

                    // check for falsey values
                    if(!aVal)
                        return 1
                    if(!bVal)
                        return -1

                    if(aVal < bVal)
                        return -1;
                    if(aVal > bVal)
                        return 1;

                    return 0;
                }
            }
        },

        computed: {
            partner_options: function () {
                let options = [];
                for (var i = 0; i < this.partners.length; i++) {
                    options.push({'value':this.partners[i].id, 'text': this.partners[i].name})
                }
                return options
            },
            consultant_options: function () {
                let options = [];
                for (var i = 0; i < this.consultants.length; i++) {
                    options.push({'value':this.consultants[i].id, 'text': this.consultants[i].name})
                }
                return options
            },
            admin_to_partner: function () {
                if (this.userRole == 'site-admin' && this.invite.to_role == 'partner-user') {
                    return true
                }
                return false
            },
            sortCodeOptions: function () {
                if (this.codeFields) {
                    return this.codeFields
                        .filter(f => f.sortable)
                        .map(f => { return { text: f.label, value: f.key } })
                }
                return new Array();
            },
            codeIsInvalidText: function() {
                if(this.selectedCode) {
                    if(!this.selectedCode.is_valid) {
                        if(!this.selectedCode.enabled) {
                            return "This registration code has been disabled.";
                        }
                        return "The partner associated with this registration code is inactive.";
                    }
                    return "Registration code is valid.";
                }
                return "No registration code has been selected";
            },
            filteredCodes: function() {
                if (this.showDisabled) {
                    return this.codes;
                } else {
                    return this.codes.filter(code => code.status === 'Enabled');
                }
            },
            filteredBySearchString: function() {
                if(this.searchString) {
                    return this.filteredCodes.filter((code) => {
                        let searchableItem = {
                            code: code.code,
                            partner: code.partner?.name || '',
                            consultant: code.ta_consultant?.name || '',
                        };
                        return JSON.stringify(searchableItem).search(new RegExp(this.searchString, "gi")) >= 0;
                    });
                } else {
                    return this.filteredCodes;
                }
            },
            pageCount: function() {
                return Math.ceil(this.filteredBySearchString.length/this.perPage);
            },
            paginatedItems: function() {
                let start = (this.currentPage-1) * this.perPage;
                return this.sortedItems.slice(start, start + this.perPage);
            },
            sortedItems: function() {
                let sortedItems = this.filteredBySearchString;
                if(this.sortField) {
                    sortedItems = sortedItems.toSorted(this.sortField.compareFn);
                    sortedItems = this.sortDesc ? sortedItems.toReversed() : sortedItems ;
                }
                return sortedItems;
            },
            store: function() {
                return useRegistrationCodesStore();
            },
            ...mapWritableState(useRegistrationCodesStore, {
                selectedCode: 'selectedCode',
                title: 'title',
                currentComponent: 'bodyComponent',
                refreshCodes: 'refreshCodes',
                showModal: 'showModal',
            }),
        },
        watch: {
            /**
             * Set remainder of sortField keys when sort dropdown is selected
             */
            "sortField.key"(newValue) {
                let newSortField = this.codeFields.find((field) => {
                    return field.key === newValue;
                });
                Object.assign(this.sortField,  { ...newSortField });
            },
            /** Do refresh of codes list when refreshCodes from store is toggled */
            "store.refreshCodes"() {
                this.getCodes();
            },
        },
        mounted() {
            if (this.userRole == 'super-user' || this.userRole == 'site-admin') {
                this.add_code_button = true
            }
            this.getPartners()
            this.getConsultants()
            this.getCodes()
        }
    }

</script>
